import { Box, Button, Flex, Stack, Text, Textarea } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import { type ReactElement, useEffect, useRef, useState } from 'react'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { isUserDefined, isUserPendingApproval } from '@/client/services/user'

import RequireLoginAndApproval from '@/components/Auth/RequireLoginAndApproval'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

import DiscussionCommentCard from './internal/DiscussionCommentCard'
import LoggedOutDiscussionView from './internal/LoggedOutDiscussionView'
import type { DiscussionCommentProps } from './types'

export default function Discussion({
  type,
  isTutor,
  comments,
  onPost,
  onDelete,
  onLoadMore,
  hasMore = false,
}: {
  type: 'session' | 'series'
  isTutor: boolean
  comments: DiscussionCommentProps[]
  onPost: (message: string) => Promise<void>
  onDelete: (id: number) => Promise<void>
  onLoadMore?: () => void
  hasMore?: boolean
}): ReactElement {
  const router = useRouter()
  const currentUser = useUser()

  const linkedCommentId = Number.parseInt(`${router.query.comment}`, 10) || 0
  const highlightRef = useRef<HTMLDivElement>(null)

  const isLoggedIn = isUserDefined(currentUser)
  const userPendingApproval = isUserPendingApproval(currentUser)

  const [commentText, setCommentText] = useState('')

  const [commentToDelete, setCommentToDelete] = useState<DiscussionCommentProps | null>(null)

  const [disableTextarea, setDisableTextarea] = useState<boolean>(false)

  const [handleDelete, isDeleting] = useAPIWrapper(
    async () => {
      if (commentToDelete == null) {
        return
      }
      await onDelete(commentToDelete.id)
      setCommentToDelete(null)
    },
    [commentToDelete, onDelete],
    'Delete Comment'
  )

  const [handlePost] = useAPIWrapper(
    async () => {
      setDisableTextarea(true)
      await onPost(commentText)
      setCommentText('')
      setDisableTextarea(false)
    },
    [commentText, onPost],
    'Post Comment'
  )

  useEffect(() => {
    highlightRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [])

  if (!isLoggedIn) {
    return <LoggedOutDiscussionView type={type} />
  }

  return (
    <>
      <ModalV3
        title="Delete Comment"
        size="lg"
        open={commentToDelete != null}
        onClose={() => setCommentToDelete(null)}
        body={() => {
          if (commentToDelete == null) {
            return null
          }
          return <Text>Are you sure you want to delete this comment?</Text>
        }}
        rightFooterContent={() => (
          <Stack direction="row" spacing="8px">
            <Button variant="outline" onClick={() => setCommentToDelete(null)}>
              Cancel
            </Button>
            <Button isLoading={isDeleting} onClick={handleDelete}>
              Yes
            </Button>
          </Stack>
        )}
      />

      <Box rounded="md" border="2px solid" borderColor="border">
        <Stack p={6} bg="bg.subtle" direction="column" spacing={2} align="flex-end">
          <Textarea
            resize="none"
            rows={2}
            onChange={e => setCommentText(e.target.value)}
            value={commentText}
            maxLength={2048}
            placeholder="Add a comment..."
            border="1px solid"
            borderColor="border.emphasized"
            isDisabled={disableTextarea || userPendingApproval}
            _focus={{
              border: '2px solid',
              borderColor: 'primary.focusRing',
            }}
            _disabled={{
              bgColor: 'bg.muted',
              color: 'fg.subtle',
              cursor: 'not-allowed',
            }}
          />
          <Tippy
            disabled={!userPendingApproval}
            content="You can't comment until your account is approved"
          >
            <RequireLoginAndApproval>
              <Button type="submit" disabled={commentText.trim().length === 0} onClick={handlePost}>
                Add Comment
              </Button>
            </RequireLoginAndApproval>
          </Tippy>
        </Stack>
        {comments.map(comment => {
          const highlighted = comment.id === linkedCommentId
          const commentRef = highlighted ? highlightRef : null
          return (
            <Box ref={commentRef} key={comment.id}>
              <DiscussionCommentCard
                type={type}
                comment={comment}
                isTutor={isTutor}
                highlighted={highlighted}
                onDelete={() => setCommentToDelete(comment)}
              />
            </Box>
          )
        })}

        {hasMore && onLoadMore != null ? (
          <Flex justifyContent="center" my={6}>
            <Button onClick={onLoadMore}>See More</Button>
          </Flex>
        ) : null}
      </Box>
    </>
  )
}
